import { useCallback, useEffect, useRef, useState } from 'react';
import { PAGE_KEY_MAP } from '../../../../../constants/templates';
import {
  ClaimLinkEditorPageEnum,
  IValidatedFieldConfig,
  IValidationErrorClearingOptions,
  TemplateValidationModalEnum,
} from '../../../../../types/templates';
import { getFieldConfig } from '../../../../../utils/templates';

interface IUseValidationErrorClearingReturn {
  /** Set of field keys that have been cleared by user editing */
  clearedErrorFields: Set<string>;
  /** Initialize a field's previous value (call on mount) */
  initializeFieldValue: (fieldKey: string, value: unknown) => void;
  /** Handle field change and clear error if applicable */
  handleFieldChange: (fieldConstant: string, newValue: unknown, pageId?: ClaimLinkEditorPageEnum) => boolean;
  /** Check if field error should be shown (not cleared) */
  shouldShowError: (fieldConstant: string, pageId?: ClaimLinkEditorPageEnum) => boolean;
  /** Get the error field key for a given field constant */
  getFieldKey: (fieldConstant: string, pageId?: ClaimLinkEditorPageEnum) => string | null;
}

export const useValidationErrorClearing = ({
  validationErrors,
  validationModal,
  activePageId,
}: IValidationErrorClearingOptions): IUseValidationErrorClearingReturn => {
  const [clearedErrorFields, setClearedErrorFields] = useState<Set<string>>(new Set());
  const prevValuesRef = useRef<Record<string, unknown>>({});
  const prevValidationModalRef = useRef(validationModal);

  // Reset cleared fields when validation modal opens (new validation ran)
  useEffect(() => {
    if (
      prevValidationModalRef.current === TemplateValidationModalEnum.None &&
      validationModal === TemplateValidationModalEnum.Error
    ) {
      setClearedErrorFields(new Set());
    }
    prevValidationModalRef.current = validationModal;
  }, [validationModal]);

  /**
   * Build error field key from config and context
   * For page fields: replaces {pageKey} placeholder with actual page key
   * For global fields: returns errorPath as-is
   */
  const buildFieldKey = useCallback(
    (config: IValidatedFieldConfig, pageId?: ClaimLinkEditorPageEnum): string => {
      // Check if field is page-scoped by presence of {pageKey} placeholder
      if (config.errorPath.includes('{pageKey}')) {
        const targetPageId = pageId || activePageId;
        if (!targetPageId) {
          return config.errorPath;
        }
        const pageKey = PAGE_KEY_MAP[targetPageId];
        return config.errorPath.replace('{pageKey}', pageKey);
      }
      // Global field - return as-is
      return config.errorPath;
    },
    [activePageId],
  );

  /**
   * Get the error field key for a given field constant
   * Returns null if field config not found
   */
  const getFieldKey = useCallback(
    (fieldConstant: string, pageId?: ClaimLinkEditorPageEnum): string | null => {
      const config = getFieldConfig(fieldConstant);
      if (!config) {
        return null;
      }
      return buildFieldKey(config, pageId);
    },
    [buildFieldKey],
  );

  /**
   * Check if a field has an error in the validation errors object
   * Navigates nested error structure using dot notation path
   */
  const hasFieldError = useCallback(
    (fieldKey: string): boolean => {
      if (!validationErrors) {
        return false;
      }

      const parts = fieldKey.split('.');
      let current: unknown = validationErrors;

      for (const part of parts) {
        if (
          current === null ||
          current === undefined ||
          typeof current !== 'object' ||
          (current as Record<string, unknown>)[part] === undefined
        ) {
          return false;
        }
        current = (current as Record<string, unknown>)[part];
      }

      // Error exists if value is truthy (non-empty string or object)
      return !!current;
    },
    [validationErrors],
  );

  /**
   * Initialize a field's previous value
   * Call this on component mount for all tracked fields
   */
  const initializeFieldValue = useCallback((fieldKey: string, value: unknown) => {
    prevValuesRef.current[fieldKey] = value;
  }, []);

  /**
   * Handle field change with automatic error clearing
   *
   * @param fieldConstant - Field constant from templates.ts
   * @param newValue - New field value
   * @param pageId - Page ID (optional, uses activePageId if not provided)
   * @returns true if error was cleared, false otherwise
   */
  const handleFieldChange = useCallback(
    (fieldConstant: string, newValue: unknown, pageId?: ClaimLinkEditorPageEnum): boolean => {
      const config = getFieldConfig(fieldConstant);

      if (!config) {
        return false;
      }

      const fieldKey = buildFieldKey(config, pageId);
      const previousValue = prevValuesRef.current[fieldKey];

      const hasTrackedValueChanged = previousValue !== undefined && previousValue !== newValue;

      if (hasTrackedValueChanged) {
        const hasError = hasFieldError(fieldKey);

        if (hasError && !clearedErrorFields.has(fieldKey)) {
          setClearedErrorFields((prev) => new Set(prev).add(fieldKey));

          // Update tracked value
          prevValuesRef.current[fieldKey] = newValue;
          return true;
        }
      }

      // Update tracked value
      prevValuesRef.current[fieldKey] = newValue;
      return false;
    },
    [buildFieldKey, hasFieldError, clearedErrorFields],
  );

  /**
   * Check if field error should be shown (not cleared by user editing)
   *
   * @param fieldConstant - Field constant from templates.ts
   * @param pageId - Page ID (optional, uses activePageId if not provided)
   * @returns true if error should be shown, false if cleared
   */
  const shouldShowError = useCallback(
    (fieldConstant: string, pageId?: ClaimLinkEditorPageEnum): boolean => {
      const config = getFieldConfig(fieldConstant);

      if (!config) {
        return true;
      }

      const fieldKey = buildFieldKey(config, pageId);
      const isCleared = clearedErrorFields.has(fieldKey);

      return !isCleared;
    },
    [buildFieldKey, clearedErrorFields],
  );

  return {
    clearedErrorFields,
    initializeFieldValue,
    handleFieldChange,
    shouldShowError,
    getFieldKey,
  };
};
